// Version 3: Hauptmenü mit Untermenüs, abgeleitet aus der Seitenliste (src/v3/pages.js).
// Hauptpunkte und Spalten kommen aus V3_SECTIONS, die Einträge je Spalte aus V3_PAGE_LIST (menu + group).
// Spalten mit „page“ (Produkte und Anlagen) listen die Kategorieseiten unter dieser Übersicht
// (`${page.path}/${id}`), der erste Eintrag führt zur Übersichtsseite selbst.
// Neue Seiten also nur in pages.js eintragen, Menü, Fußzeile und Routen folgen von selbst.
import { V3_SECTIONS, V3_PAGE_LIST, v3Page } from "./pages.js";

// Kurze Einleitung je Hauptpunkt im Mega Menü (ein Satz für Entscheider: was man dort findet)
const INTRO = {
    solutions: {
        de: "Vier Leistungen aus einer Hand: Wir analysieren Ihre Bestände, übernehmen die Abwicklung, finden passende Abnehmer und organisieren die Verwertung.",
        en: "[Placeholder: four services from one partner]",
    },
    produkte: {
        de: "Von Komponenten und Automation bis zu kompletten Maschinen und Anlagen: Wir übernehmen Ihre Bestände und bringen sie an den Markt.",
        en: "[Placeholder: from components to complete machinery]",
    },
    wissen: {
        de: "Referenzen, Praxisbeispiele und Fachartikel rund um den wirtschaftlichen Umgang mit industriellen Beständen.",
        en: "[Placeholder: references, case studies and articles]",
    },
    unternehmen: {
        de: "Wer hinter Parto steht, mit welchem Netzwerk wir arbeiten und wie Sie uns erreichen.",
        en: "[Placeholder: who we are and how to reach us]",
    },
};

// Anfrage Kasten rechts im Mega Menü, ohne Bild (Format der Navbar: title, text, cta { label, to })
const TEASER = {
    solutions: {
        to: "/kontakt?intent=assets&way=sale",
        de: { title: "Bestände bewerten lassen", text: "Senden Sie uns Ihre Bestandsliste. Wir prüfen sie und schlagen Ihnen den passenden Weg vor.", cta: "Anfrage senden" },
        en: { title: "[Have your stock assessed]", text: "[Placeholder: send us your inventory list]", cta: "[Send inquiry]" },
    },
    produkte: {
        to: "/kontakt?intent=assets&way=sale",
        de: { title: "Bestand anbieten", text: "Komponenten, Maschinen oder ganze Anlagen: Bestandsliste oder Fotos hochladen, wir melden uns persönlich.", cta: "Anfrage senden" },
        en: { title: "[Offer your stock]", text: "[Placeholder: upload your list or photos]", cta: "[Send inquiry]" },
    },
    wissen: {
        to: "/kontakt?mode=rueckruf",
        de: { title: "Ihren Fall besprechen", text: "Sie möchten wissen, was mit Ihren Beständen möglich ist? Wir rufen Sie gern zurück.", cta: "Rückruf anfordern" },
        en: { title: "[Discuss your case]", text: "[Placeholder: we will call you back]", cta: "[Request a callback]" },
    },
    unternehmen: {
        to: "/kontakt?mode=rueckruf",
        de: { title: "Persönlich sprechen", text: "Ein Ansprechpartner begleitet Ihr Projekt von der ersten Anfrage bis zum Abschluss.", cta: "Rückruf anfordern" },
        en: { title: "[Talk to us]", text: "[Placeholder: one contact person for your project]", cta: "[Request a callback]" },
    },
};

const ALL = { de: "Alle Kategorien", en: "[All categories]" };

// Einträge einer Spalte: Seiten der Gruppe oder Abschnitte der zugehörigen Übersichtsseite
const itemsOf = (sectionId, g) => {
    if (g.page) {
        const page = v3Page(g.page);
        if (!page) return [];
        return [
            { to: page.path, de: ALL.de, en: ALL.en },
            ...(page.sections || []).map((s) => ({ to: `${page.path}/${s.id}`, de: s.de, en: s.en })),
        ];
    }
    return V3_PAGE_LIST
        .filter((p) => p.menu === sectionId && p.group === g.id)
        .map((p) => ({ to: p.path, de: p.label.de, en: p.label.en }));
};

// Form: { id, to, de, en, intro, groups: [{ id, de, en, page?, items: [{ to, de, en }] }] }
export const V3_MENU = V3_SECTIONS.map((s) => ({
    id: s.id,
    to: s.to,
    de: s.de,
    en: s.en,
    intro: INTRO[s.id] || { de: "", en: "" },
    groups: s.groups.map((g) => ({ id: g.id, de: g.de, en: g.en, page: g.page, items: itemsOf(s.id, g) })).filter((g) => g.items.length),
}));

// Landingpages (ohne Menüeintrag, für Fußzeile und Verweise)
export const V3_LANDINGS = V3_PAGE_LIST.filter((p) => p.template === "landing").map((p) => ({ to: p.path, de: p.label.de, en: p.label.en }));

export const V3_PLACEHOLDER = {
    de: {
        overview: "Zur Übersicht",
        text: "[Platzhalter: Inhalt dieser Seite folgt]",
        cta: "Anfrage senden",
        notFound: "Seite nicht gefunden",
        notFoundText: "Diese Adresse gibt es nicht. Über die Startseite oder eine Anfrage kommen Sie direkt weiter.",
        home: "Zur Startseite",
        sections: "Kategorien",
    },
    en: {
        overview: "[Placeholder: To overview]",
        text: "[Placeholder: content for this page will follow]",
        cta: "[Send inquiry]",
        notFound: "[Page not found]",
        notFoundText: "[Placeholder: this address does not exist]",
        home: "[Back to home]",
        sections: "[Categories]",
    },
};

// Menüdaten im Format des Mega Menüs der Navbar.
// Das Mega Menü zeigt zwei Spalten. Bei mehr als zwei Gruppen hängt die dritte Gruppe als Unterabschnitt (sub)
// an der zweiten Spalte, z. B. Solutions: „Unsere Leistungen“ | „Ihre Herausforderung“ + „Für Ihre Rolle“.
export const v3Menus = (lang) => {
    const k = lang === "en" ? "en" : "de";
    const L = V3_PLACEHOLDER[k];
    const links = V3_MENU.map((m) => ({ to: m.to, label: m[k], id: `v3-${m.id}`, menu: m.id }));
    const menus = Object.fromEntries(V3_MENU.map((m) => {
        const cols = m.groups.map((g) => ({ title: g[k], items: g.items.map((i) => ({ label: i[k], to: i.to })) }));
        // Wunsch 22.10.2026: kein Anfrage Kasten im Mega Menü, dafür bis zu drei Spalten nebeneinander
        const packed = cols;
        const t = TEASER[m.id];
        return [m.id, {
            title: m[k],
            intro: m.intro[k],
            all: { label: L.overview, to: m.to },
            teaser: null, // Kasten entfernt (Wunsch 22.10.2026), Texte bleiben in TEASER als Vorrat
            cols: packed,
        }];
    }));
    return { links, menus };
};
